import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchMilestones } from '../api/milestonesApi'
import ProjectUpdatesView from './ProjectUpdatesView'

export default function ProjectTimeline({ projectId, projectCreatedAt }) {
  const [activeTab, setActiveTab] = useState('timeline')

  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => fetchMilestones(projectId),
    enabled: !!projectId,
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <div className="w-4 h-4 bg-secondary-200 rounded-full" />
            <div className="flex-1 h-16 bg-secondary-100 rounded" />
          </div>
        ))}
      </div>
    )
  }

  const allEvents = [
    ...milestones.map(m => ({
      ...m,
      date: m.completed_date || m.target_date,
    })),
    {
      id: 'project-created',
      title: 'Project Created',
      description: 'Development project launched on platform',
      date: projectCreatedAt,
      isCompleted: true,
      isProjectStart: true,
    },
  ]

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusColor = (event) => {
    if (event.isProjectStart || event.is_completed) return 'bg-green-500'
    if (event.target_date && new Date(event.target_date) < new Date()) return 'bg-yellow-500'
    return 'bg-secondary-300'
  }

  const getStatusText = (event) => {
    if (event.isProjectStart) return 'Completed'
    if (event.is_completed) return `Completed ${formatDate(event.completed_date)}`
    if (event.target_date) {
      const targetDate = new Date(event.target_date)
      const today = new Date()
      if (targetDate < today) return 'Overdue'
      return `Target: ${formatDate(event.target_date)}`
    }
    return 'Date TBD'
  }

  return (
    <div className="card p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-xl font-semibold text-secondary-900 mb-1">Project Timeframe</h3>
        <p className="text-sm text-secondary-600">
          Track progress and read updates from the developer
        </p>
      </div>

      <div className="flex space-x-1 mb-4 sm:mb-6 border-b border-secondary-200">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`px-4 sm:px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
            activeTab === 'timeline'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-600 hover:text-secondary-900'
          }`}
        >
          Timeline
        </button>
        <button
          onClick={() => setActiveTab('updates')}
          className={`px-4 sm:px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
            activeTab === 'updates'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-secondary-600 hover:text-secondary-900'
          }`}
        >
          Updates
        </button>
      </div>

      {activeTab === 'timeline' && (
        <div>
          {allEvents.length === 1 ? (
            <div className="text-center py-8 text-secondary-600">
              <p>No milestones have been set for this project yet.</p>
              <p className="text-sm mt-2">Check back later for updates on the project progress.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {allEvents.map((event, index) => (
                <div key={event.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-4 h-4 rounded-full ${getStatusColor(event)} flex-shrink-0 mt-1`}>
                      {event.is_completed && (
                        <svg className="w-full h-full text-white p-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    {index < allEvents.length - 1 && (
                      <div className="w-0.5 h-full bg-secondary-200 flex-1 min-h-[40px]" />
                    )}
                  </div>

                  <div className="flex-1 pb-6">
                    <div className="flex items-start justify-between gap-4 mb-1">
                      <h4 className="font-semibold text-secondary-900">{event.title}</h4>
                      <span className={`text-sm px-2 py-0.5 rounded-full whitespace-nowrap ${
                        event.is_completed || event.isProjectStart
                          ? 'bg-green-100 text-green-700'
                          : event.target_date && new Date(event.target_date) < new Date()
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-secondary-100 text-secondary-600'
                      }`}>
                        {getStatusText(event)}
                      </span>
                    </div>
                    {event.description && (
                      <p className="text-sm text-secondary-600 mt-1">{event.description}</p>
                    )}
                    {event.isProjectStart && (
                      <p className="text-sm text-secondary-500 mt-1">
                        {formatDate(event.date)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'updates' && (
        <ProjectUpdatesView projectId={projectId} />
      )}
    </div>
  )
}
